import cn from "classnames"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Input } from "reactstrap"
import {
    AccordionWithChildren,
    addModalThunk,
    DropDownList,
    formatLocalDate,
    getDistinct,
    groupBy,
    InputLabel,
    numberToFixedDecimals,
    sortAscending,
    StandardButtonWithSpinner,
    sumArray,
    TextButton,
    useSelector,
} from "swiipe.portal.shared"
import { getBaseTranslationLanguage } from "../../../services/translationService"
import { translationActions, translationSelectors } from "../../../store/reducers/translationSlice"
import {
    changeTranslationContextThunk,
    removeTranslationGlossaryEntryThunk,
    setTranslationGlossaryEntriesForLanguageThunk,
    syncTranslationChangesThunk,
    translateTextsThunk,
} from "../../../store/thunks/translationsThunks"
import useReduxDispatch from "../../../store/useReduxDispatch"
import { ITranslationContextDto } from "../../../type/translations/ITranslationContextDto"
import { ITranslationGlossaryEntryDto } from "../../../type/translations/ITranslationGlossaryEntryDto"
import { ITranslationLanguageConfig } from "../../../type/translations/ITranslationLanguageConfig"
import "./TranslationsActionsDeepL.scss"

export interface ITranslationsActionsDeepLProps {
    languageConfigs: ITranslationLanguageConfig[]
}

const unsupportedLanguages = ["nn"]

const TranslationsActionsDeepL = ({ languageConfigs }: ITranslationsActionsDeepLProps) => {
    const { t } = useTranslation()
    const dispatch = useReduxDispatch()
    const items = useSelector(translationSelectors.items) ?? []
    const groups = useMemo(() => groupBy(items, (i) => i.group), [items])
    const selections = useSelector(translationSelectors.selections)
    const usage = useSelector(translationSelectors.usage)

    const [localContexts, setLocalContexts] = useState<ITranslationContextDto[]>([])
    const [addContext, setAddContext] = useState(false)
    const formContexts: ITranslationContextDto[] = [
        ...(addContext
            ? [
                  localContexts.find((lc) => !lc.contextId)
                      ? localContexts.find((lc) => !lc.contextId)!
                      : { contextId: "", context: "", name: "" },
              ]
            : []),
        ...localContexts.filter((c) => c.contextId),
    ]
    const contextsFromSlice = useSelector(translationSelectors.contexts)
    const contexts = contextsFromSlice ?? []
    const [contextForTranslation, setContextForTranslation] = useState(contexts[0] ?? formContexts[0])

    const [localGlossaryEntries, setLocalGlossaryEntries] = useState<Record<string, ITranslationGlossaryEntryDto[]>>({})
    const [addGlossaryEntry, setAddGlossaryEntry] = useState<
        { language: string; entry: ITranslationGlossaryEntryDto } | undefined
    >()

    useEffect(() => {
        const groups = getDistinct(selections.map((s) => s.group))
        if (contexts.find((c) => c.name == "Terms") && groups.every((g) => g.startsWith("Terms->"))) {
            setContextForTranslation(contexts.find((c) => c.name == "Terms")!)
        }
        if (contexts.find((c) => c.name == "Email") && groups.every((g) => g === "EmailTemplates")) {
            setContextForTranslation(contexts.find((c) => c.name == "Email")!)
        }
        if (contexts.find((c) => c.name == "SMS") && groups.every((g) => g === "SmsTemplates")) {
            setContextForTranslation(contexts.find((c) => c.name == "SMS")!)
        }
    }, [selections])

    const glossaryEntries = useSelector(translationSelectors.glossaryEntries)
    useEffect(() => {
        setLocalContexts(contexts)
    }, [contextsFromSlice])
    useEffect(() => {
        setLocalGlossaryEntries(glossaryEntries ?? {})
    }, [glossaryEntries])

    const [translateLanguage, setTranslateLanguage] = useState<string>(
        languageConfigs.find((lc) => lc.shortCulture === "da")!.language
    )

    const selectedItems = selections.filter((s) => !s.languages || s.languages.includes(translateLanguage))

    return (
        <div className="translation-actions-deepl">
            <AccordionWithChildren leftAlignHeader header={<h4>DeepL Translation</h4>}>
                <div>
                    <h6>Usage</h6>
                    <div>
                        Period:{" "}
                        <b>
                            {formatLocalDate(usage?.periodStart, "medium")} - {formatLocalDate(usage?.periodEnd, "medium")}
                        </b>
                    </div>
                    <div>
                        Used:{" "}
                        <b>
                            {numberToFixedDecimals(
                                ((usage?.charactersUsedInPeriod ?? 0) / (usage?.characterPeriodLimit ?? 1)) * 100,
                                2
                            )}
                            %
                        </b>{" "}
                        ({usage?.charactersUsedInPeriod} / {usage?.characterPeriodLimit})
                    </div>
                </div>
                <AccordionWithChildren className="translation-actions-deepl__content" leftAlignHeader header={<h5>Contexts</h5>}>
                    <div className="translation-actions-deepl__content-inner">
                        {!addContext && (
                            <div className="translation-actions-deepl__row">
                                <StandardButtonWithSpinner
                                    containerClass="translation-actions-deepl__action-button"
                                    title="Add Context"
                                    onClick={async () => {
                                        setAddContext(true)
                                    }}
                                />
                            </div>
                        )}
                        <div className="translation-actions-deepl__contexts">
                            {formContexts.map((c) => {
                                return (
                                    <div key={c.contextId} className="translation-actions-deepl__context">
                                        <InputLabel title="Name">
                                            <Input
                                                className={cn(
                                                    !!c.name &&
                                                        contexts.find(
                                                            (gc) => gc.contextId === c.contextId && c.name !== gc.name
                                                        ) &&
                                                        "translation-actions-deepl__input--changed"
                                                )}
                                                value={c.name}
                                                onChange={(e) => {
                                                    const index = formContexts.findIndex((lc) => lc.contextId === c.contextId)
                                                    const ccc = [
                                                        ...formContexts.slice(0, index),
                                                        { ...formContexts[index], name: e.currentTarget.value },
                                                        ...formContexts.slice(index + 1),
                                                    ]
                                                    setLocalContexts(ccc)
                                                }}
                                            />
                                        </InputLabel>
                                        <InputLabel title="Context">
                                            <Input
                                                className={cn(
                                                    !!c.context &&
                                                        contexts.find(
                                                            (gc) => gc.contextId === c.contextId && c.context !== gc.context
                                                        ) &&
                                                        "translation-actions-deepl__input--changed"
                                                )}
                                                type="textarea"
                                                value={c.context}
                                                onChange={(e) => {
                                                    const index = formContexts.findIndex((lc) => lc.contextId === c.contextId)
                                                    setLocalContexts([
                                                        ...formContexts.slice(0, index),
                                                        { ...formContexts[index], context: e.currentTarget.value },
                                                        ...formContexts.slice(index + 1),
                                                    ])
                                                }}
                                            />
                                        </InputLabel>
                                        <div className="translation-actions-deepl__row">
                                            <StandardButtonWithSpinner
                                                disabled={
                                                    !!contexts.find(
                                                        (gc) =>
                                                            gc.contextId === c.contextId &&
                                                            c.context === gc.context &&
                                                            c.name === gc.name
                                                    )
                                                }
                                                containerClass="translation-actions-deepl__action-button"
                                                title="Save"
                                                onClick={async () => {
                                                    await dispatch(changeTranslationContextThunk(c))
                                                    if (!c.contextId) {
                                                        setAddContext(false)
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                        <div></div>
                    </div>
                </AccordionWithChildren>
                <AccordionWithChildren className="translation-actions-deepl__content" leftAlignHeader header={<h5>Glossary</h5>}>
                    <div className="translation-actions-deepl__content-inner">
                        <div className="translation-actions-deepl__glossary">
                            {languageConfigs
                                .filter((lc) => !unsupportedLanguages.includes(lc.shortCulture))
                                .map((lc) => {
                                    const addingEntry =
                                        addGlossaryEntry?.language === lc.shortCulture ? addGlossaryEntry.entry : undefined
                                    const hasValidAdd =
                                        !!addingEntry?.fromText &&
                                        !!addingEntry?.toText &&
                                        !localGlossaryEntries?.[lc.shortCulture]?.find(
                                            (ge) => ge.fromText === addingEntry?.fromText
                                        )
                                    const entries = localGlossaryEntries[lc.shortCulture] ?? []
                                    const changedEntries = entries.filter(
                                        (e) =>
                                            e.toText !==
                                            glossaryEntries?.[lc.shortCulture]?.find((ge) => ge.fromText === e.fromText)?.toText
                                    )
                                    return (
                                        <AccordionWithChildren
                                            key={lc.language}
                                            className="translation-actions-deepl__glossary-language"
                                            leftAlignHeader
                                            header={<b>{lc.shortCulture}</b>}
                                        >
                                            <div className="translation-actions-deepl__glossary-language-inner">
                                                {addingEntry && (
                                                    <div className="translation-actions-deepl__add-glossary-entry">
                                                        <div className="translation-actions-deepl__row">
                                                            <InputLabel title="From text (en)">
                                                                <Input
                                                                    className={cn(
                                                                        addingEntry.fromText &&
                                                                            "translation-actions-deepl__input--changed"
                                                                    )}
                                                                    value={addingEntry.fromText}
                                                                    onChange={(e) => {
                                                                        setAddGlossaryEntry({
                                                                            language: lc.shortCulture,
                                                                            entry: {
                                                                                fromLanguage: "en",
                                                                                toLanguage: lc.shortCulture,
                                                                                toText: addGlossaryEntry?.entry?.toText ?? "",
                                                                                fromText: e.currentTarget.value,
                                                                            },
                                                                        })
                                                                    }}
                                                                />
                                                            </InputLabel>
                                                            <InputLabel title={`To text (${lc.shortCulture})`}>
                                                                <Input
                                                                    className={cn(
                                                                        addingEntry.toText &&
                                                                            "translation-actions-deepl__input--changed"
                                                                    )}
                                                                    value={addingEntry.toText}
                                                                    onChange={(e) => {
                                                                        setAddGlossaryEntry({
                                                                            language: lc.shortCulture,
                                                                            entry: {
                                                                                fromLanguage: "en",
                                                                                toLanguage: lc.shortCulture,
                                                                                toText: e.currentTarget.value,
                                                                                fromText: addGlossaryEntry?.entry?.fromText ?? "",
                                                                            },
                                                                        })
                                                                    }}
                                                                />
                                                            </InputLabel>
                                                            <TextButton
                                                                className="translation-actions-deepl__action-button translation-actions-deepl__action-button-remove"
                                                                text="Remove"
                                                                textDecoration="underline"
                                                                fontSize="big"
                                                                onClick={async () => {
                                                                    setAddGlossaryEntry(undefined)
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                )}
                                                {!addingEntry && (
                                                    <div className="translation-actions-deepl__row">
                                                        <TextButton
                                                            className="translation-actions-deepl__action-button"
                                                            text="Add"
                                                            textDecoration="underline"
                                                            fontSize="big"
                                                            onClick={async () => {
                                                                setAddGlossaryEntry({
                                                                    language: lc.shortCulture,
                                                                    entry: {
                                                                        fromLanguage: "en",
                                                                        toLanguage: lc.shortCulture,
                                                                        fromText: "",
                                                                        toText: "",
                                                                    },
                                                                })
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                                {sortAscending(entries, (e) => e.fromText.toLowerCase()).map((entry) => {
                                                    const savedEntry = glossaryEntries?.[lc.shortCulture]?.find(
                                                        (ge) => ge.fromText === entry.fromText
                                                    )

                                                    return (
                                                        <div key={entry.fromText} className="translation-actions-deepl__row">
                                                            <div>{entry.fromText}</div>
                                                            <InputLabel title={`To text (${lc.shortCulture})`}>
                                                                <Input
                                                                    className={cn(
                                                                        (entry.toText ?? "") !== (savedEntry?.toText ?? "") &&
                                                                            "translation-actions-deepl__input--changed"
                                                                    )}
                                                                    value={entry.toText ?? ""}
                                                                    onChange={(e) => {
                                                                        const index = entries.findIndex(
                                                                            (en) => en.fromText === entry.fromText
                                                                        )
                                                                        setLocalGlossaryEntries({
                                                                            ...localGlossaryEntries,
                                                                            [lc.shortCulture]: [
                                                                                ...entries.slice(0, index),
                                                                                {
                                                                                    ...entry,
                                                                                    toText: e.currentTarget.value,
                                                                                },
                                                                                ...entries.slice(index + 1),
                                                                            ],
                                                                        })
                                                                    }}
                                                                />
                                                            </InputLabel>
                                                            <TextButton
                                                                className="translation-actions-deepl__action-button translation-actions-deepl__action-button-remove"
                                                                text="Remove"
                                                                textDecoration="underline"
                                                                fontSize="big"
                                                                onClick={async () => {
                                                                    await dispatch(
                                                                        removeTranslationGlossaryEntryThunk({
                                                                            fromLanguage: "en",
                                                                            toLanguage: lc.shortCulture,
                                                                            fromText: entry.fromText,
                                                                            toText: entry.toText,
                                                                        })
                                                                    )
                                                                }}
                                                            />
                                                        </div>
                                                    )
                                                })}
                                                <div className="translation-actions-deepl__row">
                                                    <StandardButtonWithSpinner
                                                        disabled={!hasValidAdd && changedEntries.length === 0}
                                                        containerClass="translation-actions-deepl__action-button"
                                                        title="Save"
                                                        onClick={async () => {
                                                            const changes = [
                                                                ...(hasValidAdd ? [addingEntry] : []),
                                                                ...changedEntries,
                                                            ]

                                                            await dispatch(setTranslationGlossaryEntriesForLanguageThunk(changes))
                                                            if (addGlossaryEntry?.language === lc.shortCulture) {
                                                                setAddGlossaryEntry(undefined)
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </AccordionWithChildren>
                                    )
                                })}
                        </div>
                        <div></div>
                    </div>
                </AccordionWithChildren>
                <h5>AI Translation</h5>
                <div className="translation-actions-deepl__row">
                    <InputLabel title="Language to translate to">
                        <DropDownList
                            options={[
                                ...(languageConfigs
                                    .filter((lc) => !unsupportedLanguages.includes(lc.shortCulture))
                                    .map((lc) => ({
                                        text: lc.label,
                                        value: lc.language,
                                    })) ?? []),
                            ]}
                            onChange={(e) => {
                                setTranslateLanguage(e.currentTarget.value)
                            }}
                            value={translateLanguage}
                        />
                    </InputLabel>
                    <InputLabel title="Context">
                        <DropDownList
                            options={[
                                ...(formContexts.map((lc) => ({
                                    text: lc.name,
                                    value: lc.contextId,
                                })) ?? []),
                            ]}
                            onChange={(e) => {
                                setContextForTranslation(formContexts.find((c) => c.contextId === e.currentTarget.value)!)
                            }}
                            value={contextForTranslation?.contextId}
                        />
                    </InputLabel>
                </div>
                <div>
                    {selectedItems.length} selected texts to translate for the{" "}
                    {languageConfigs.find((lc) => lc.language === translateLanguage)!.shortCulture} language
                </div>
                <StandardButtonWithSpinner
                    containerClass="translation-actions-deepl__action-button mt-3"
                    onClick={async () => {
                        const baseLanguage = getBaseTranslationLanguage()

                        const itemsToTranslate = items.filter(
                            (i) =>
                                i.translations.some((t) => t.language === baseLanguage) &&
                                selectedItems.some((si) => si.itemId === i.id)
                        )
                        const texts = itemsToTranslate.map(
                            (i) => i.translations.find((t) => t.language === baseLanguage)!.content
                        )
                        const modalResult = await dispatch(
                            addModalThunk({
                                type: "verify",
                                title: "Translate using DeepL",
                                acceptButtonText: "Translate",
                                text: `Are you sure you want to translate ${texts.length} texts with a total of ${sumArray(
                                    texts,
                                    (t) => t.length
                                )} characters?`,
                            })
                        )
                        if (modalResult.type === "accepted") {
                            await dispatch(
                                translateTextsThunk(itemsToTranslate, translateLanguage, contextForTranslation?.contextId ?? "")
                            )
                        }
                    }}
                    title="Translate selected items"
                />
                <StandardButtonWithSpinner
                    containerClass="translation-actions-deepl__action-button mt-3"
                    isDelete
                    onClick={async () => {
                        const itemsToClear = items.filter((i) => selectedItems.some((si) => si.itemId === i.id))
                        dispatch(
                            translationActions.clearChanges({
                                itemIds: itemsToClear.map((i) => i.id),
                                language: translateLanguage,
                                type: "TranslationService",
                            })
                        )

                        dispatch(syncTranslationChangesThunk())
                    }}
                    title="Clear translations for selection"
                />
            </AccordionWithChildren>
        </div>
    )
}

export default TranslationsActionsDeepL
